import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  filter,
  finalize,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { ConnectorsService } from '../_generated/api/connectors.service';
import { LayoutActions } from '../layout/store/layout.actions';
import { ConnectorsActions } from './connectors.actions';
import { connectorConnectUrl, ConnectorProvider } from './connectors.models';

/** Map untrusted callback reasons to known translation keys, with a generic fallback. */
const AUTHORIZATION_FAILURE_KEYS: Record<string, string> = {
  // eslint-disable-next-line camelcase
  access_denied: 'CONNECTORS.ERRORS.DECLINED',
  // eslint-disable-next-line camelcase
  invalid_state: 'CONNECTORS.ERRORS.EXPIRED',
  // eslint-disable-next-line camelcase
  missing_code: 'CONNECTORS.ERRORS.GENERIC',
};

/** Shared with assets/connector-callback.js, which posts a popup sign-in's outcome on it. */
const CONNECTOR_POPUP_CHANNEL = 'squadbox-connectors';

interface ConnectorPopupResult {
  provider: ConnectorProvider;
  reason: string | null;
}

/** Room for the providers' consent pages without dropping them into their mobile layouts. */
const POPUP_WIDTH = 520;
const POPUP_HEIGHT = 720;

/**
 * How long a loaded list answers `loadConnectors` without a request. A status changes through a
 * consent or a disconnect, and both force `reloadConnectors`, so a list this young is current. The
 * window bounds what a caller that asks in a loop can cost: on 2026-10-20 one hs dev tab sent
 * `GET /v1/connectors` every ~525 ms for five minutes, each ask following the previous answer.
 */
export const CONNECTORS_REUSE_WINDOW_MS = 10_000;

@Injectable()
export class ConnectorsEffects {
  private readonly actions$ = inject(Actions);
  private readonly connectorsApiService = inject(ConnectorsService);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);

  /** The provider this tab opened a sign-in popup for; another tab's popup only refreshes the list here. */
  private popupProvider: ConnectorProvider | null = null;

  /** When the last list request succeeded; `null` until one has, and again after a failure. */
  private lastLoadedAt: number | null = null;
  /** A list request is out, so a `loadConnectors` waits for its answer rather than sending another. */
  private isLoadInFlight = false;

  /**
   * `reloadConnectors` always fetches, and replaces a request in flight. `loadConnectors` joins a
   * request in flight and reuses a list younger than CONNECTORS_REUSE_WINDOW_MS, so only a failed or
   * an old list costs a request.
   */
  loadConnectors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ConnectorsActions.loadConnectors,
        ConnectorsActions.reloadConnectors,
      ),
      filter(
        ({ type }) =>
          type === ConnectorsActions.reloadConnectors.type ||
          !this.isLoadInFlight,
      ),
      switchMap(({ type }) => {
        if (
          type === ConnectorsActions.loadConnectors.type &&
          this.lastLoadedAt !== null &&
          Date.now() - this.lastLoadedAt < CONNECTORS_REUSE_WINDOW_MS
        ) {
          return of(ConnectorsActions.loadConnectorsSkipped());
        }
        this.isLoadInFlight = true;
        return this.connectorsApiService
          .connectorsControllerListConnectorsV1()
          .pipe(
            tap(() => (this.lastLoadedAt = Date.now())),
            map((connectors) =>
              ConnectorsActions.loadConnectorsSuccess({ connectors }),
            ),
            catchError((error: Error) => {
              this.lastLoadedAt = null;
              return of(
                ConnectorsActions.loadConnectorsFailure({
                  error: error.message,
                }),
              );
            }),
            // Also on a replacing `reloadConnectors`: switchMap unsubscribes this request before it
            // starts the next one, which sets the flag again.
            finalize(() => (this.isLoadInFlight = false)),
          );
      }),
    ),
  );

  loadConnectorsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConnectorsActions.loadConnectorsFailure),
        tap(() => {
          this.toastrService.error(
            this.translateService.instant('CONNECTORS.ERRORS.LOAD') as string,
            undefined,
            { closeButton: true, disableTimeOut: true },
          );
        }),
      ),
    { dispatch: false },
  );

  /**
   * Start consent in a popup so the person stays where they are; its last page reports back on
   * CONNECTOR_POPUP_CHANNEL (`popupReturned$`). A blocked popup falls back to a full-tab redirect,
   * which returns through the chats page's `?connector=` parameters.
   */
  connectProvider$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConnectorsActions.connectProvider),
        tap(({ provider }) => {
          const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
          const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;
          // Opened blank so its opener can be cut while it is still same-origin: nothing reads it,
          // and the provider's pages should not be able to navigate this tab.
          const popup = window.open(
            '',
            'squadbox-connector',
            `popup,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`,
          );
          if (!popup) {
            window.location.href = connectorConnectUrl(provider);
            return;
          }
          popup.opener = null;
          popup.location.href = `${connectorConnectUrl(provider)}?display=popup`;
          this.popupProvider = provider;
        }),
      ),
    { dispatch: false },
  );

  /**
   * A popup's outcome is reported like a redirect's, in the tab that opened it. Every open tab hears
   * the channel; the others only re-read the list, since the person's connections just changed.
   */
  popupReturned$ = createEffect(() =>
    new Observable<ConnectorPopupResult>((subscriber) => {
      const channel = new BroadcastChannel(CONNECTOR_POPUP_CHANNEL);
      channel.onmessage = (event: MessageEvent<ConnectorPopupResult>) =>
        subscriber.next(event.data);
      return () => channel.close();
    }).pipe(
      map(({ provider, reason }) => {
        if (provider !== this.popupProvider) {
          return ConnectorsActions.reloadConnectors();
        }
        this.popupProvider = null;
        return ConnectorsActions.authorizationReturned({ provider, reason });
      }),
    ),
  );

  disconnectProvider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConnectorsActions.disconnectProvider),
      switchMap(({ provider }) =>
        this.connectorsApiService
          .connectorsControllerDisconnectV1(provider)
          .pipe(
            map(() =>
              ConnectorsActions.disconnectProviderSuccess({ provider }),
            ),
            catchError((error: Error) =>
              of(
                ConnectorsActions.disconnectProviderFailure({
                  provider,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  disconnectProviderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConnectorsActions.disconnectProviderSuccess),
      tap(() => {
        this.toastrService.success(
          this.translateService.instant(
            'CONNECTORS.TOASTS.DISCONNECTED',
          ) as string,
        );
      }),
      // Re-read rather than patch: one source of truth for a row's status.
      map(() => ConnectorsActions.reloadConnectors()),
    ),
  );

  disconnectProviderFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConnectorsActions.disconnectProviderFailure),
        tap(() => {
          this.toastrService.error(
            this.translateService.instant(
              'CONNECTORS.ERRORS.DISCONNECT',
            ) as string,
            undefined,
            { closeButton: true, disableTimeOut: true },
          );
        }),
      ),
    { dispatch: false },
  );

  /**
   * Reports the outcome of a consent round trip. Consent always starts in the Connectors settings, so
   * the outcome — from the popup, or from a redirect that left the app — lands back there. The consent
   * may have changed the list, so it is reloaded first, and the settings pane's own ask joins that request.
   */
  authorizationReturned$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConnectorsActions.authorizationReturned),
      tap(({ reason }) => {
        if (reason === null) {
          this.toastrService.success(
            this.translateService.instant(
              'CONNECTORS.TOASTS.CONNECTED',
            ) as string,
          );
          return;
        }
        // Own keys only: `?error=constructor` would otherwise hand ngx-translate a function.
        const key = Object.hasOwn(AUTHORIZATION_FAILURE_KEYS, reason)
          ? AUTHORIZATION_FAILURE_KEYS[reason]
          : 'CONNECTORS.ERRORS.GENERIC';
        this.toastrService.error(
          this.translateService.instant(key) as string,
          undefined,
          { closeButton: true, disableTimeOut: true },
        );
      }),
      mergeMap(() => [
        ConnectorsActions.reloadConnectors(),
        LayoutActions.openSettings({
          source: 'connector-return',
          pane: 'connectors',
        }),
      ]),
    ),
  );
}
